import PollingManager from '@/ghost/service/Requests/PollingManager'
export default class WebSocketManager {
  CLOSE_CODES = {
    CLIENT: 3000
  }

  constructor(url, options = {}) {
    this.url = this.parseWebSocketUrl(url)
    this.maxRetries = options.maxRetries ?? 3
    this.initialDelay = options.initialDelay ?? 1000
    this.maxDelay = options.maxDelay ?? 30000
    this.listenerDelay = options.listenerDelay ?? 3000
    this.backoffFactor = options.backoffFactor ?? 1.2
    this.debug = options.debug ?? false

    this.retryCount = 0
    this.socket = null
    this.poll = null
    this.open = false
    this.listeners = {}
    this.connect()
  }

  connect() {
    this.socket = new WebSocket(this.url)

    // Fired when the WebSocket connection is open
    this.socket.onopen = event => {
      if (!this.open) {
        this.open = true
        this.retryCount = 0
      }
      this.emit('open', event)
    }

    // Fired when data is received through a WebSocket
    this.socket.onmessage = event => {
      this.emit('message', event.data)
    }

    // Fired when a connection with a WebSocket has been closed because of an error,
    // such as when some data couldn't be sent
    this.socket.onerror = event => {
      // We can receive a connection lost error when switching between the bank application and the browser
      // in mobile. As there is no way to differenciate this error from other WebSocket errors, we assume
      // that any error is a connection lost and try to reconnect immediately. As it enters in the loop, we
      // will never emit an error.
      // See: KJS-6120
      this.reconnect()
    }

    // Fired when the WebSocket connection is closed
    this.socket.onclose = event => {
      if (event.code !== this.CLOSE_CODES.CLIENT && this.open) {
        this.reconnect()
      }
    }

    // Internet connection is lost (avoid connection retries + restart counter when connection is back)
    window.onoffline = event => {
      this.open = false
      this.socket.close(this.CLOSE_CODES.CLIENT, 'Connection lost')
    }

    // Internet connection is regained
    window.ononline = event => {
      this.connect()
    }
  }

  /**
   * Reconnection with exponential backoff. The delay is calculated as:
   * min(initialDelay * (backoffFactor ^ retryCount), maxDelay)
   */
  reconnect() {
    this._log(
      `Connection lost. Attempting to reconnect... (Retry ${this.retryCount}/${this.maxRetries})`
    )

    if (this.retryCount < this.maxRetries) {
      const delay = Math.min(
        this.initialDelay * Math.pow(this.backoffFactor, this.retryCount),
        this.maxDelay
      )

      this.retryCount++

      setTimeout(() => {
        this.connect()
      }, delay)
    } else {
      if (!this.poll)
        this.poll = new PollingManager(this.getPollingUrl(), {
          emit: this.emit.bind(this),
          debug: this.debug
        })
    }
  }

  on(event, callback) {
    if (!this.listeners[event]) this.listeners[event] = []
    this._log(`Registering listener for event: ${event}`)
    this.listeners[event].push(callback)
  }

  emit(event, payload) {
    if (!this.listeners[event])
      console.warn('[WebSocketManager] No listeners for event:', event)
    this._log(`Emitting event: ${event} with payload: ${payload}`)
    this.listeners[event]?.forEach(cb => cb(payload))
  }

  /**
   * Closes the WebSocket connection or connection attempt, if any.
   * If the connection is already CLOSED, this method does nothing
   * @param {boolean} withEmit - Whether to emit a 'close' event to listeners (default: true)
   */
  close(withEmit = true) {
    this._log(`Closing WebSocket connection (withEmit: ${withEmit})`)
    if (this.open && withEmit)
      this.emit('close', {
        code: this.CLOSE_CODES.CLIENT,
        reason: 'Connection closed by client'
      })
    if (this.poll) {
      this.poll.stop()
      this.poll = null
    }
    this.open = false
    this.socket.onclose = null
  }

  /**
   * Build the polliing URL from the WebSocket URL by replacing the path and protocol accordingly.
   * @example:
   *  input -> wss://payzen-inte02.lyra-labs.fr/checkout/v3/web/bizum-23a190fe-0ad2-473a-a389-7af5614e59b1/webpayments/transactions/fab72b77b7ea4c648b91872e49bd4e1d/websocket-status
   *  output -> https://payzen-inte02.lyra-labs.fr/checkout/v3/web/wero-246fdb00-a2c3-45a4-b7ba-5e1cdff77c10/webpayments/transactions/2c5185bc4c9b402fb421592147fb897b/polling-status
   */
  getPollingUrl() {
    return this.url
      .replace('/websocket-status', '/polling-status')
      .replace('wss://', 'https://')
      .replace('ws://', 'http://')
  }

  /**
   * Old devices might throw an error when trying to connect with an unsupported protocol (e.g., wss://).
   * https://developer.mozilla.org/en-US/docs/Web/API/WebSockets_API#browser_compatibility
   * In that case, we replace the protocol.
   * @see: KJS-6118
   */
  parseWebSocketUrl(url) {
    if (url.match(/(http(s?):\/\/)/g)) {
      this._log(`WebSocket URL uses HTTP/HTTPS, replacing protocol`)
      return url.replace('http://', 'ws://').replace('https://', 'wss://')
    }

    return url
  }

  _log(message) {
    if (this.debug) {
      // eslint-disable-next-line no-console
      console.log(`[WebSocketManager] ${message}`)
    }
  }
}
